<template lang='pug'>
.checkout-wrap.checkout-scroll
  client-only
    template(#ssr)
      loading(height="55vh")
    loading(v-if="!loaded" height="55vh")
    #cart.main-container(v-else :class="{'rogue-scroll-y': rogue.appContext, 'has-applepay': applePay}")
      .cart-head
        h2 {{ __('Your Cart') }}
        .cart-empty(v-if="cartQty === 0") {{ __('Your cart is empty') }}
        .cart-count(v-else) {{ cartQty === 1 ? __('1 Item') : __('{count} Items', {count: cartQty}) }}
      cart-messages.section(v-if="order" :order="order")
      .cart-main
        .left-col.section
          cart-compatibility
          .cart-item(v-for="item in customCartItems" :key="item._id")
            CustomOrderItem(:buyRequest="item" :bundleBuyRequests="bundleCartBuyRequests" list="cart" theme="cart" :order="order")
          .cart-item(v-for="item in standardCartItems" :key="item._id")
            OrderItem(:buyRequest="item" list="cart" theme="cart")
          .cart-links(v-if="cartQty > 0")
            button.link(@click="emptyCart()") {{__('Empty Cart')}}
            OrderPdfForm(
              #default="scope"
              v-if="cartQty > 0"
              :order="order"
            )
              button.link(v-if="!rogue.appContext && loggedIn" @click="printInvoice(scope)") {{__('Print Order Invoice')}}
          saved-for-later.section
            .cart-item(v-for="item in customSflItems" :key="item._id")
              CustomOrderItem(:buyRequest="item" :bundleBuyRequests="bundleSflBuyRequests" list="sfl" theme="cart")
            .cart-item(v-for="item in standardSflItems" :key="item._id")
              OrderItem(:buyRequest="item" list="sfl")
          .move-all-container.cart-links(v-if="sfl.items.length > 0")
            button.link.move-all(@click="moveAllSfl(rogue)") {{__('Move All Available Items to Cart')}}
        .right-col(v-if="cartQty > 0")
          order-summary.section(:collapse="false" :rogue-order="rogueOrder")
          .total.section
            .text {{order?.totals.tax !== null ? __('Order Total') : __('Est. Total')}}
            .val {{ rogue.currency(order?.totals.grandTotalIncludingTax || order?.totals.total)  }}
          .checkout-buttons(:class="{section: !rogue.appContext}")
            RogueButton.checkout-button(
              color="red"
              width="full"
              :size="rogue.appContext ? 'l' : null"
              :disabled="checkoutDisabled"
              @click="confirmCheckout"
            ) {{ __('Checkout') }}
            .digital-wallet-row.section(v-if="rogueOrder && (applePay || googlePay)")
              ApplePay.express-pay-button(
                v-if="applePay"
                v-show="applePayAvailable"
                :order="rogueOrder"
                :cart="cart"
                :defaultShippingMethod="shippingMethod"
                :disabled="checkoutDisabled"
                @available="applePayAvailable = $event"
              )
              GooglePay.express-pay-button(
                v-if="googlePay"
                v-show="googlePayAvailable"
                :order="rogueOrder"
                :disabled="checkoutDisabled"
                @available="googlePayAvailable = $event"
              )
      ProductCarousel.recommendations(
        v-if="loaded"
        title="You Might Also Like"
        :widget="alsoBoughtWidget"
        :podId="cartItemIds.length ? PODS.CUSTOMERS_ALSO_BOUGHT : PODS.SEARCH_DEFAULT"
        :itemIds="cartItemIds.length ? cartItemIds : undefined"
      )
        template(#product="{product}")
          Price(:product="product" theme="list")
</template>

<script lang="ts" setup>
import { onMounted, computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import CartMessages from 'Src/js/components/cart/cart-messages.vue'
import SavedForLater from 'Src/js/components/cart/saved-for-later.vue'
import OrderPdfForm from 'Src/js/components/order-pdf-form.vue'
import type { IOrderShipping } from '@rogue/universal'
import type { RogueOrder } from '@orders'
import { useCustomItemFiltering } from '@/js/composables/useCustomFiltering'
import { useConfigStore } from '@/js/stores/configStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useEventStore } from '@/js/stores/eventStore'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'
import metadata from '@/js/helpers/metadata'
import moveAllSfl from '@/js/helpers/move-all-sfl'
import { getOosItems, getSimpleForBuyRequest, productHasChanged } from '@/js/helpers/buy-request'
import OrderSummary from '@/js/components/cart/order-summary.vue'
import { widgets } from '@/js/services/rfk/types'
import { PODS } from '@/js/services/cnstrc/constants'
import { getCnstrcItemId } from '@/js/services/cnstrc/util'
import CartCompatibility from '@/js/components/compatibility/cart-compatibility.vue'
import type { BuyRequest, Order } from '@/js/services/types/order'

const configStore = useConfigStore()
const customerStore = useCustomerStore()
const eventStore = useEventStore()
const listStore = useListStore()
const productStore = useProductStore()
const uiStore = useUIStore()
const rogue = useRogue()
const route = useRoute()
const router = useRouter()
const { customCartItems, customSflItems, standardCartItems, standardSflItems, bundleCartBuyRequests, bundleSflBuyRequests } = useCustomItemFiltering()
const rogueOrder = ref<RogueOrder>()
const applePayAvailable = ref(false)
const googlePayAvailable = ref(false)

useHead(metadata({ robots: 'noindex', title: rogue.__('Cart') }, rogue))

const alsoBoughtWidget = ref(widgets.CART)

const profile = computed(() => eventStore.profile)

const loaded = ref(false)

const order = computed(() => rogueOrder.value?.data)

const shipping = computed<IOrderShipping[]>(() => order.value?.shipping || [])

const cart = computed(() => listStore.cart)

const sfl = computed(() => listStore.sfl)

const cartQty = computed(() => listStore.cartQty)

const cartItemIds = computed(() =>
  cart.value.items.map(i => getCnstrcItemId(i, productStore.products)).filter(Boolean) as string[]
)

const oosItems = computed(() => getOosItems(cart.value.items, productStore.products, rogue))

const changedItems = computed(() => cart.value.items.filter(item => productHasChanged(item, productStore.products)))

const checkoutDisabled = computed<boolean>(() => oosItems.value.length > 0 || changedItems.value.length > 0 || cartQty.value === 0)

const cartContainsProgram = computed(() => rogueOrder.value?.state.payment.hasProgram)

const loggedIn = computed(() => customerStore.loggedIn)

const hasSubscription = computed(() => cart.value.items.map(item => getSimpleForBuyRequest(item, rogue))
  .some(product => product?.isProgram && product.attributes.subscription_interval))

const cartContainsBTWB = computed(() => cart.value.items.map(item => getSimpleForBuyRequest(item, rogue))
  .some(product => product?.attributes?.program_delivery === 'BTWB'))

const allItemsAreVirtual = computed(() => cart.value.items.length > 0
  && cart.value.items.every(item => getSimpleForBuyRequest(item, rogue)?.type === 'virtual'))

const canUseExpressPayment = computed<boolean>(() => !hasSubscription.value && (!cartContainsProgram.value || loggedIn.value))
const applePay = computed<boolean>(() => configStore.applePay && canUseExpressPayment.value)
const googlePay = computed<boolean>(() => configStore.googlePay && canUseExpressPayment.value && !allItemsAreVirtual.value)

const shippingMethod = computed(() => order.value?.shipping?.[0]?.shippingCode)

onMounted(async () => {
  const { initOrder } = await import('@/js/pages/checkout/checkout')
  rogueOrder.value = await initOrder(rogue)

  // Load SFL products for non-app context (already loaded above in app context)
  if (!rogue.appContext) {
    await productStore.loadAll(sfl.value?.items.map(i => i.productId), rogue)
  }

  const cartLoad = route.query.crtld
  if (cartLoad && cartQty.value === 0) {
    // Load the cart (used for US -> CA store redirects)
    try {
      const { items, shippingAddress } = await rogue.fetch<{items: BuyRequest[], shippingAddress: Order['shippingAddress']}>(`/svc_ssr/store-switch?key=${cartLoad}`, 'GET')
      await listStore.addListItems(rogue, 'cart', items, false)
      rogueOrder.value!.updateShippingAddress(shippingAddress)
      router.replace({ query: {} })
    } catch (e: any) {
      rogue.log.warn('Unable to load items', e)
      // Error loading cart
    }
  }

  if (cartContainsBTWB.value) {
    await eventStore.loadProfile(rogue)
  }

  loaded.value = true
})

function printInvoice(scope: { print: () => void }) {
  if (shippingMethod.value) {
    scope.print()
  } else {
    listStore.addMessage(rogue, {
      list   : 'cart',
      text   : 'Order Invoice requires a shipping estimate.',
      status : 'warning',
    })
  }
}

function emptyCart() {
  listStore.emptyListItems(rogue, 'cart')
}

function confirmCheckout() {
  if (!profile.value.linked && cartContainsBTWB.value) {
    uiStore.showOverlay('programLogin', rogue)

    return
  }

  if (loggedIn.value || !cartContainsProgram.value) {
    router.push('/checkout')
  } else {
    uiStore.showOverlay('programLogin', rogue)
  }
}
</script>

<style lang="scss" scoped>
#cart {
  padding: $space-xl 0 $space-m 0;
}

@include not-mobile() {
  .cart-main {
    display: flex;
  }
  .left-col {
    flex-basis: 66.6%;
    padding-right: $space-s;
    align-self: flex-start;
    border-right: $border-width-thin solid $color-gray;
  }
  .right-col {
    flex-basis: 33.3%;
    padding-left: $space-l;
  }
}

.cart-count {
  color: $color-black-light;
  font-size: $size-xs;
}

.section {
  margin-top: $space-s;
}

.cart-item {
  padding: $space-s 0;
  border-top: $border-width-thin solid $color-gray;
}

.recommendations {
  margin-top: $space-l;
  margin-bottom: $space-l;
}

.total {
  font-size: $size-m;
  display: flex;
  justify-content: space-between;

  .text {
    color: $color-black-light;
  }

  .val {
    font-weight: $weight-bold;
  }
}

.cart-links {
  font-size: $size-xxs;
  border-top: $border-width-thin solid $color-gray;
  border-bottom: $border-width-thin solid $color-gray;
  display: flex;
  justify-content: space-between;
  padding: $space-xs 0;

  .link {
    color: $color-gray-dark;
  }
}

.move-all-container {
  display: block;
  text-align: center;
}

.digital-wallet-row {
  display: flex;
  gap: $space-xs;

  .express-pay-button {
    flex: 1;
    min-width: 0;

    :deep(.gpay-card-info-container) {
      min-width: 0;
    }
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios, .platform-android {
  .cart-links {
    margin-bottom: $space-s;
    justify-content: center;
  }

  &:not(.rogue-app-rn) #cart.main-container {
    padding: $app-gap-default 0 #{$app-button-bar-height * 2};
    height: 100%;
    position: fixed;
    left: 0;
    right: 0;
  }

  #cart.main-container {
    margin: 0;
    padding-top: $app-gap-default;
  }

  .left-col.section {
    padding: $app-gap-default 0;
  }

  .cart-head, .cart-main {
    margin: 0 $space-s;
  }

  .recommendations {
    margin-left: $space-s;
    margin-right: $space-s;

    :deep(.scroller-wrap) {
      padding-left: 0;
      padding-right: $space-s;
    }
  }

  .checkout-buttons {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    margin: 0;
    background-color: white;
  }

  #cart.has-applepay .checkout-buttons {
    border-radius: $space-s $space-s 0 0;
    box-shadow: 0px -2px 10px 0px rgba(162, 162, 162, 0.35);
    padding: $space-s;
  }
}

// Rogue App v3
.rogue-app-rn {
  .checkout-wrap {
    height: calc(100vh - (#{$app-button-bar-height} + var(--above-header-height) + var(--checkout-banner-height) + var(--cookie-agree-height)));
  }
  #cart {
    height: 100vh;
    padding-bottom: $app-button-bar-height;

    &.has-applepay {
      padding-bottom: 156px;
    }
  }
  .recommendations {
    max-width: calc(100vw - (#{$space-s} * 2));
  }
}
</style>
